import { z } from "zod";
import {
   BillingPeriod,
   PaymentMethod,
} from "../constants/billing";

const requiredText = (message) =>
   z.string().trim().min(1, message);

const validDate = (message) =>
   requiredText(message).refine((value) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
      const parsedDate = new Date(`${value}T00:00:00Z`);
      return (
         !Number.isNaN(parsedDate.getTime()) &&
         parsedDate.toISOString().slice(0, 10) === value
      );
   }, message);

const validDateTime = (message) =>
   requiredText(message).refine((value) => {
      const match = value.match(
         /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/
      );
      if (!match) return false;

      const [, year, month, day, hour, minute, second = "0"] = match;
      const parsedDate = new Date(0);
      parsedDate.setUTCFullYear(Number(year), Number(month) - 1, Number(day));

      return (
         parsedDate.getUTCFullYear() === Number(year) &&
         parsedDate.getUTCMonth() === Number(month) - 1 &&
         parsedDate.getUTCDate() === Number(day) &&
         Number(hour) <= 23 &&
         Number(minute) <= 59 &&
         Number(second) <= 59
      );
   }, message);

export const licensePaymentSchema = z.object({
   billingUuid: requiredText("A cobrança atual não foi identificada.").regex(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
      "O identificador da cobrança é inválido."
   ),
   paymentMethod: z
      .string()
      .refine(
         (value) => Object.values(PaymentMethod).includes(value),
         "Selecione uma forma de pagamento válida."
      ),
   paidAt: requiredText("A data e hora do pagamento são obrigatórias.").refine(
      (value) =>
         /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value) &&
         !Number.isNaN(Date.parse(value)),
      "Informe uma data e hora válidas."
   ),
});

export const licenseBillingSchema = z
   .object({
      period: z
         .string()
         .refine(
            (value) => Object.values(BillingPeriod).includes(value),
            "Selecione um ciclo de cobrança válido."
         ),
      dueDate: validDate("Informe uma data de vencimento válida."),
      amount: z
         .number()
         .finite("Informe um valor válido.")
         .positive("O valor deve ser maior que zero."),
      totalAmount: z
         .number()
         .finite("O valor previsto não está disponível.")
         .nonnegative("O valor previsto não está disponível."),
      hasCalculatedTotal: z.boolean(),
      discountReason: z.string().nullable().optional(),
   })
   .superRefine((values, context) => {
      if (!values.hasCalculatedTotal) {
         context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["amount"],
            message: "Aguarde o cálculo do valor antes de salvar.",
         });
      }

      if (values.amount < values.totalAmount && !values.discountReason?.trim()) {
         context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["discountReason"],
            message: "Informe o motivo do desconto.",
         });
      }
   });

const scheduledLicenseActionSchema = z.object({
   reason: requiredText("O motivo é obrigatório."),
   scheduledFor: validDateTime("Informe uma data e hora válidas."),
});

export const licenseBlockSchema = scheduledLicenseActionSchema;
export const licenseSuspendSchema = scheduledLicenseActionSchema;

export const licenseFeaturesSchema = z
   .object({
      featureIds: z.array(requiredText("Selecione recursos válidos.")),
      hasBackupFeature: z.boolean(),
      enabledBackup: z.boolean().optional(),
      enabledRotation: z.boolean().optional(),
      backupSize: z.number().finite().optional(),
   })
   .superRefine((values, context) => {
      if (
         values.hasBackupFeature &&
         values.enabledBackup &&
         !(values.backupSize > 0)
      ) {
         context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["backupSize"],
            message: "O tamanho do backup deve ser maior que zero.",
         });
      }

      if (
         values.hasBackupFeature &&
         values.enabledRotation &&
         !values.enabledBackup
      ) {
         context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["enabledRotation"],
            message: "Habilite o backup para ativar a rotação.",
         });
      }
   });

export const licenseTransferOwnerSchema = z.object({
   ownerId: requiredText("Selecione o proprietário de destino."),
});

export const licenseAllocationSchema = z.object({
   unitId: requiredText("Selecione uma unidade para alocar a licença."),
});

export const licenseSerialNumberSchema = z.object({
   serialNumber: requiredText("O número de série é obrigatório."),
});

export const licenseDueDateSchema = z.object({
   billingId: requiredText("A cobrança atual não foi identificada."),
   dueDate: validDate("Informe uma data de vencimento válida."),
});

export function getZodFieldErrors(error) {
   return error.issues.reduce((fieldErrors, issue) => {
      const field = issue.path[0];
      if (field != null && fieldErrors[field] == null) {
         fieldErrors[field] = issue.message;
      }
      return fieldErrors;
   }, {});
}
